// client/src/components/UserList.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate, Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HubIcon from '@mui/icons-material/Hub';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AppBarHeader from './AppBarHeader';
import CommunityMembersGif from './banners/Community-Members.gif';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import BlockButton from './BlockButton';
import PresenceIndicator from './messages/PresenceIndicator';
import FollowButton from './social/FollowButton';
import FederationFollowButton from './social/FederationFollowButton';

function UserList() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversationTitle, setConversationTitle] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [remoteAcct, setRemoteAcct] = useState('');
  const [remoteAcctError, setRemoteAcctError] = useState('');

  // Discovery / instances tab
  const [activeTab, setActiveTab] = useState(0);
  const [peers, setPeers] = useState([]);
  const [peersLoading, setPeersLoading] = useState(false);
  const [selectedPeer, setSelectedPeer] = useState(null);
  const [peerUsers, setPeerUsers] = useState([]);
  const [peerUsersLoading, setPeerUsersLoading] = useState(false);
  const [peerUsersError, setPeerUsersError] = useState('');

  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();

  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;
  const isAdmin = userDecoded ? userDecoded.isAdmin : false;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/users/community');

        const processedUsers = response.data.map(user => {
          if (user.isRemote && user.profileImage) {
            // Remote actor avatars come from foreign instances — proxy them
            user.profileImage = `/api/proxy-fed-image?url=${encodeURIComponent(user.profileImage)}`;
          } else if (user.profileImage && user.profileImage.startsWith('/')) {
            const baseServerUrl = axiosInstance.getBaseServerUrl();
            user.profileImage = `${baseServerUrl}${user.profileImage}`;
          }
          return user;
        });

        const visibleUsers = processedUsers.filter(user => !user.isDisabled && String(user._id) !== String(userId));
        const filteredUsers = isAdmin
          ? visibleUsers
          : visibleUsers.filter(user => !user.isHidden);

        setUsers(filteredUsers);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to fetch user list. Please try again later.');
        setLoading(false);
      }
    };

    fetchUsers();
    fetchCategories();
  }, [isAdmin]);

  useEffect(() => {
    if (featureFlags.enableFederation && activeTab === 1) {
      fetchPeers();
    }
  }, [featureFlags.enableFederation, activeTab]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchPeers = async () => {
    try {
      setPeersLoading(true);
      const response = await axiosInstance.get('/fed/peers');
      setPeers(response.data);
    } catch (err) {
      console.error('Error fetching peers:', err);
    } finally {
      setPeersLoading(false);
    }
  };

  const handleBrowseInstance = async (peer) => {
    setSelectedPeer(peer);
    setPeerUsers([]);
    setPeerUsersError('');
    setPeerUsersLoading(true);
    try {
      const response = await axiosInstance.get(`/fed/peer-directory?domain=${encodeURIComponent(peer.domain)}`);
      setPeerUsers(response.data.users || []);
    } catch (err) {
      setPeerUsersError(err.response?.data?.error || 'Could not load users from this instance.');
    } finally {
      setPeerUsersLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleRemoteUserLookup = () => {
    const trimmed = remoteAcct.trim();
    if (!trimmed.includes('@') || trimmed.startsWith('@')) {
      setRemoteAcctError('Enter a handle in user@domain format, e.g. alice@other.example.com');
      return;
    }
    setRemoteAcctError('');
    navigate(`/remote-profile?acct=${encodeURIComponent(trimmed)}`);
  };

  const handleOpenMessageDialog = (user) => {
    setSelectedUser(user);
    setConversationTitle('');
    setSelectedCategory('');
    setMessageDialogOpen(true);
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setSelectedUser(null);
    setConversationTitle('');
    setSelectedCategory('');
  };

  const handleStartConversation = async () => {
    if (!conversationTitle.trim() || !selectedUser) return;

    try {
      const response = await axiosInstance.post('/messages/threads', {
        targetUserId: selectedUser._id,
        title: conversationTitle.trim(),
        categoryId: selectedCategory || null,
        context: { type: 'collection' }
      });
      handleCloseMessageDialog();
      navigate(`/messages/thread/${response.data._id}`);
    } catch (err) {
      console.error('Error creating thread:', err);
      alert(err.response?.data?.error || 'Failed to start conversation. Please try again.');
    }
  };

  return (
    <div>
      <AppBarHeader
        isAdmin={isAdmin}
        userId={userId}
        onLogout={handleLogout}
      />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        <Typography variant="h4" sx={{ marginTop: 4, marginBottom: 4, textAlign: 'center' }}>
          <img
            src={CommunityMembersGif}
            alt="Community Members"
            style={{ maxWidth: '50%', height: 'auto' }}
          />
        </Typography>

        {/* Federation: user search + tabs */}
        {featureFlags.enableFederation && (
          <>
            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Find a user on another instance"
                  placeholder="user@other.instance.com"
                  value={remoteAcct}
                  onChange={e => { setRemoteAcct(e.target.value); setRemoteAcctError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleRemoteUserLookup()}
                  error={!!remoteAcctError}
                  helperText={remoteAcctError || 'Enter a federated handle to view their profile and follow them'}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<PersonSearchIcon />}
                onClick={handleRemoteUserLookup}
                sx={{ mt: 0.5 }}
              >
                Find User
              </Button>
            </Paper>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<PeopleIcon />} iconPosition="start" label="Community" />
              <Tab icon={<HubIcon />} iconPosition="start" label="Instances" />
            </Tabs>
          </>
        )}

        {/* ── Community Tab ── */}
        {activeTab === 0 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={3}>
              {users.map((user) => {
                const isRemote = user.isRemote === true;
                // Remote usernames are stored as "user@domain" already — use as-is
                const remoteAcctHandle = isRemote ? user.username : null;
                const profileLink = isRemote && remoteAcctHandle
                  ? `/remote-profile?acct=${encodeURIComponent(remoteAcctHandle)}`
                  : `/users/${user._id}`;

                return (
                  <Grid item xs={12} sm={6} md={4} key={user._id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                        <Box
                          component={Link}
                          to={profileLink}
                          sx={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}
                        >
                          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                            <Avatar
                              alt={user.username}
                              src={user.profileImage || null}
                              sx={{ width: 100, height: 100, mx: 'auto' }}
                              imgProps={{
                                onError: (e) => { e.target.onerror = null; }
                              }}
                            >
                              {user.username ? user.username[0].toUpperCase() : '?'}
                            </Avatar>
                            {!isRemote && (
                              <PresenceIndicator
                                lastSeen={user.lastSeen}
                                isOnline={false}
                              />
                            )}
                          </Box>
                          <Typography variant="h6" gutterBottom sx={{ '&:hover': { color: 'primary.main' } }}>
                            {remoteAcctHandle || user.username}
                          </Typography>
                          {isRemote && user.remoteInstance && (
                            <Chip
                              label={user.remoteInstance}
                              size="small"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                          )}
                        </Box>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'center', padding: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%' }}>
                          {isRemote ? (
                            <>
                              <Button
                                variant="outlined"
                                component={Link}
                                to={profileLink}
                                size="small"
                                fullWidth
                              >
                                Profile
                              </Button>
                              {featureFlags.enableFollowSystem && featureFlags.enableFederation ? (
                                <FederationFollowButton
                                  shadowUserId={user._id}
                                  acct={remoteAcctHandle}
                                  actorUrl={user.federationId}
                                  size="small"
                                />
                              ) : <Box />}
                            </>
                          ) : (
                            <>
                              {/* Row 1: Profile | Message */}
                              <Button
                                variant="outlined"
                                onClick={() => navigate(`/users/${user._id}`)}
                                size="small"
                                fullWidth
                              >
                                Profile
                              </Button>
                              {featureFlags.enableMessageBoardFeature && user._id !== userId ? (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<MessageIcon />}
                                  onClick={() => handleOpenMessageDialog(user)}
                                  size="small"
                                  fullWidth
                                >
                                  Message
                                </Button>
                              ) : <Box />}

                              {/* Row 2: Collections | Wishlists */}
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate(`/collections/${user._id}`)}
                                size="small"
                                fullWidth
                              >
                                Collections
                              </Button>
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate(`/wishlists/${user._id}`)}
                                size="small"
                                fullWidth
                              >
                                Wishlists
                              </Button>

                              {/* Row 3: Follow | Block */}
                              {featureFlags.enableFollowSystem && user._id !== userId ? (
                                <Box sx={{ '& .MuiButton-root': { width: '100%' } }}>
                                  <FollowButton targetUserId={user._id} currentUserId={userId} />
                                </Box>
                              ) : <Box />}
                              {featureFlags.enableMessageBoardFeature && user._id !== userId ? (
                                <Box sx={{ '& .MuiButton-root': { width: '100%' } }}>
                                  <BlockButton targetUserId={user._id} variant="outlined" size="small" />
                                </Box>
                              ) : <Box />}
                            </>
                          )}
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* ── Instances Tab ── */}
        {activeTab === 1 && featureFlags.enableFederation && (
          <Box>
            {peersLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : peers.length === 0 ? (
              <Alert severity="info">
                No known instances yet. Follow users from other instances to discover them here.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {peers.map((peer) => (
                  <Grid item xs={12} sm={6} md={4} key={peer.domain}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <HubIcon color="primary" />
                          <Typography variant="h6">{peer.instanceName || peer.domain}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {peer.domain}
                        </Typography>
                        {peer.instanceDescription && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {peer.instanceDescription}
                          </Typography>
                        )}
                        {peer.userCount != null && (
                          <Chip
                            label={`${peer.userCount} user${peer.userCount !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                      <CardActions sx={{ p: 2, gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleBrowseInstance(peer)}
                          disabled={peerUsersLoading && selectedPeer?.domain === peer.domain}
                        >
                          Browse Users
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<OpenInNewIcon />}
                          href={`https://${peer.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Visit
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Browse instance users panel */}
            {selectedPeer && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6">
                    Users on {selectedPeer.instanceName || selectedPeer.domain}
                  </Typography>
                  <Button size="small" onClick={() => setSelectedPeer(null)}>
                    Close
                  </Button>
                </Box>

                {peerUsersLoading && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                {peerUsersError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{peerUsersError}</Alert>
                )}
                {!peerUsersLoading && peerUsers.length > 0 && (
                  <Grid container spacing={2}>
                    {peerUsers.map((u) => (
                      <Grid item xs={12} sm={6} md={4} key={u.acct}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Avatar
                              src={u.profileImage ? `/api/proxy-fed-image?url=${encodeURIComponent(u.profileImage)}` : undefined}
                              alt={u.username}
                              sx={{ width: 64, height: 64, mx: 'auto', mb: 1 }}
                            >
                              {u.username ? u.username[0].toUpperCase() : '?'}
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {u.acct}
                            </Typography>
                            {u.bio && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {u.bio}
                              </Typography>
                            )}
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/remote-profile?acct=${encodeURIComponent(u.acct)}`)}
                            >
                              View Profile
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {!peerUsersLoading && !peerUsersError && peerUsers.length === 0 && (
                  <Alert severity="info">No public users listed on this instance.</Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* New Conversation Dialog */}
      <Dialog open={messageDialogOpen} onClose={handleCloseMessageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Start a Conversation with {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Conversation Topic"
            fullWidth
            variant="outlined"
            value={conversationTitle}
            onChange={(e) => setConversationTitle(e.target.value)}
            placeholder="e.g., Trading PS2 games, Best RPGs, etc."
            helperText="Give your conversation a descriptive title"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && conversationTitle.trim()) {
                handleStartConversation();
              }
            }}
            sx={{ mb: 2 }}
          />
          {categories.length > 0 && (
            <FormControl fullWidth variant="outlined">
              <InputLabel>Category (Optional)</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category (Optional)"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <Button
            onClick={handleStartConversation}
            variant="contained"
            disabled={!conversationTitle.trim()}
          >
            Start Conversation
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default UserList;
